import { useEffect } from 'react';
import { Box, Stack, Typography } from '@mui/material';

import { FHButton } from '../../../../../../../components/base';
import {
  DOCUMENT_UPLOAD_ACCEPTED_IMAGE_TYPES,
  DocumentUploadDialog,
  QuestionnaireSectionLabel,
  useDocumentUpload,
  useDocumentUploadSectionContext,
} from '../../../../../common';
import { useFormReadOnly } from '../../../../../../common';
import { FORTA_GRAY5 } from '../../../../../../../utils/colors';
import { Themes } from '../../../../../../../constants';
import { REFERRAL_REPORT_REQUIREMENTS } from '../constants';
import UploadedDocumentsSection from './UploadedDocumentsSection';

import type { DiagnosisReportSectionProps } from './types';

export default function DiagnosisReportBody({
  disabled,
  uploadDocument,
  documents,
  deleteDocument,
}: DiagnosisReportSectionProps) {
  const { isDesktopMobileCaptureHandoff } = useDocumentUploadSectionContext();
  const readOnly = useFormReadOnly();
  const {
    open,
    isDialogOpen,
    closeDialog,
    handleDialogSelect,
    initialFile,
    cameraInputRef,
    handleCameraChange,
  } = useDocumentUpload({ onFile: uploadDocument });

  useEffect(() => {
    if (readOnly) {
      closeDialog();
    }
  }, [readOnly, closeDialog]);

  return (
    <>
      <Box>
        <QuestionnaireSectionLabel variant="body1" sx={{ mb: { xs: 1, md: 2 } }}>
          Report requirements
        </QuestionnaireSectionLabel>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          alignItems="flex-end"
          justifyContent="space-between"
          gap={2}
        >
          <Box component="ul" sx={{ flex: 1, pl: 3, m: 0, color: FORTA_GRAY5 }}>
            {REFERRAL_REPORT_REQUIREMENTS.map((requirement) => (
              <Typography component="li" key={requirement} sx={{ fontSize: 14 }}>
                {requirement}
              </Typography>
            ))}
          </Box>
          {!isDesktopMobileCaptureHandoff && !readOnly && (
            <FHButton colorTheme={Themes.Primary} disabled={disabled} onClick={open}>
              Add document
            </FHButton>
          )}
        </Stack>
      </Box>
      {documents.length > 0 && (
        <UploadedDocumentsSection
          disabled={disabled || readOnly}
          documents={documents}
          onDelete={deleteDocument}
        />
      )}
      {!isDesktopMobileCaptureHandoff && !readOnly && (
        <>
          <DocumentUploadDialog
            existingFileNames={documents.map((document) => document.title)}
            initialFile={initialFile}
            onClose={closeDialog}
            onSelect={handleDialogSelect}
            open={isDialogOpen}
            title="Upload referral document"
          />
          <Box
            component="input"
            accept={Object.keys(DOCUMENT_UPLOAD_ACCEPTED_IMAGE_TYPES).join(',')}
            capture="environment"
            onChange={handleCameraChange}
            ref={cameraInputRef}
            sx={{ display: 'none' }}
            type="file"
          />
        </>
      )}
    </>
  );
}
